/**
 * What the file picker offers and what the back accepts, in one list. The back decides by
 * the extension of the name, so this is the list of extensions and nothing cleverer: a
 * `.xlsx` the browser calls `application/octet-stream` is still an `.xlsx`.
 */
export const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
  '.txt',
  '.xlsx',
  '.xlsm',
  '.xls',
  '.csv',
  '.tsv',
  '.docx',
] as const;

/** Mirrors the API's limits. Checked here only to say it before the round trip. */
export const MAX_FILES = 5;

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
}

export function isAcceptedName(name: string): boolean {
  return (ACCEPTED_EXTENSIONS as readonly string[]).includes(extensionOf(name));
}

const PHOTO_EXTENSIONS: readonly string[] = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];

export function isPhotoName(name: string): boolean {
  return PHOTO_EXTENSIONS.includes(extensionOf(name));
}

/**
 * How long a reading is expected to take, in seconds, from the names of the files.
 *
 * Measured on 2026-09-12 against the real provider (`docs/extraction.md` of the back): a
 * text or a PDF answers in about 5 to 10 s, one photo in about 30 s, five photos in one
 * reading in about 40 s (the provider looks at them together, so it is not five times
 * one). Rounded up, because an estimate that runs short reads as a failure and one
 * that runs long reads as a pleasant surprise.
 */
export function estimatedReadingSeconds(names: readonly string[]): number {
  const photos = names.filter(isPhotoName).length;
  if (photos === 0) return 15;
  return 45 + (photos - 1) * 10;
}

/** The estimate above, in words a person can act on. */
export function readingEstimateText(names: readonly string[]): string {
  const photos = names.filter(isPhotoName).length;
  if (photos === 0) return 'Suele tardar unos segundos.';
  if (photos === 1) return 'Una foto tarda alrededor de un minuto en leerse.';
  return `Las fotos tardan alrededor de un minuto en leerse; con ${photos} puede ser algo más.`;
}

/**
 * Why a chosen set of files cannot be sent, or null.
 *
 * Only what can be known from the names and the count: the size limits are the API's
 * and the API's message names the file, so they are not second guessed here.
 */
export function problemWith(files: File[]): string | null {
  if (files.length === 0) return null;
  if (files.length > MAX_FILES) return `Como mucho ${MAX_FILES} archivos por presupuesto.`;
  const rejected = files.find((file) => !isAcceptedName(file.name));
  if (rejected) {
    return `«${rejected.name}» no se puede leer. Vale un PDF, una foto (JPG, PNG, WebP, HEIC), un Excel (XLSX, XLS, CSV) o un Word (DOCX).`;
  }
  return null;
}
